import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", service: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-gold/15 bg-obsidian-800 px-4 py-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-gold/60";

export default function Contact({ preselectedService }) {
  const content = useContent();
  const c = content.contact;
  const allServices = content.services.categories.flatMap((cat) => cat.services.map((s) => s.name));
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preselectedService) {
      setForm((f) => ({
        ...f,
        service: preselectedService,
        message: `Merhaba, "${preselectedService}" hizmeti için teklif almak istiyorum.`,
      }));
    }
  }, [preselectedService]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Lütfen adınızı ve telefon numaranızı girin.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Talebiniz alındı. En kısa sürede size dönüş yapacağız.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Gönderim başarısız oldu. Lütfen WhatsApp üzerinden deneyin.");
    } finally {
      setSending(false);
    }
  };

  const waText = encodeURIComponent(
    `Merhaba Reya Desing, ${form.service || "hizmetleriniz"} hakkında teklif almak istiyorum.`
  );

  const infoRows = [
    { icon: Phone, label: "Telefon", value: c.phone },
    { icon: Mail, label: "E-posta", value: c.email },
    { icon: MapPin, label: "Konum", value: c.address },
  ];

  return (
    <section id="iletisim" data-testid="contact-section" className="relative overflow-hidden bg-obsidian-800 py-24 sm:py-32 lg:py-40">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[28rem] w-[28rem] rounded-full bg-gold/8 blur-[130px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">{c.eyebrow}</p>
          <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl lg:text-5xl">
            {c.titlePre} <span className="text-gold">{c.titleGold}</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400 sm:text-base">
            {c.intro}
          </p>

          <a
            data-testid="whatsapp-btn"
            href={`https://wa.me/${c.whatsapp}?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-10 flex w-fit items-center gap-3 rounded-full border border-gold/40 bg-gold/10 px-7 py-4 font-display text-base font-bold text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian"
          >
            <MessageCircle className="h-5 w-5" />
            WhatsApp ile Teklif Al
          </a>

          <div className="mt-12 space-y-5">
            {infoRows.map((row) => (
              <div key={row.label} className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/20 bg-obsidian">
                  <row.icon className="h-4 w-4 text-gold" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{row.label}</p>
                  <p className="text-sm text-zinc-200" data-testid={`contact-info-${row.label === "Telefon" ? "phone" : row.label === "E-posta" ? "email" : "address"}`}>{row.value}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="glass-panel gold-glow rounded-3xl p-6 sm:p-10"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Ad Soyad *</label>
              <input
                data-testid="contact-name-input"
                className={inputCls}
                placeholder="Adınız Soyadınız"
                value={form.name}
                onChange={set("name")}
              />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Telefon *</label>
              <input
                data-testid="contact-phone-input"
                className={inputCls}
                placeholder="05XX XXX XX XX"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-gold">E-posta</label>
            <input
              data-testid="contact-email-input"
              type="email"
              className={inputCls}
              placeholder="ornek@firma.com"
              value={form.email}
              onChange={set("email")}
            />
          </div>
          <div className="mt-5">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Hizmet</label>
            <select
              data-testid="contact-service-select"
              className={`${inputCls} appearance-none`}
              value={form.service}
              onChange={set("service")}
            >
              <option value="">Hizmet seçin…</option>
              {allServices.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
              <option value="Diğer">Diğer</option>
            </select>
          </div>
          <div className="mt-5">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Mesajınız</label>
            <textarea
              data-testid="contact-message-input"
              rows={4}
              className={`${inputCls} resize-none`}
              placeholder="Projenizden kısaca bahsedin…"
              value={form.message}
              onChange={set("message")}
            />
          </div>
          <button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={sending}
            className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-gold py-4 font-display text-base font-bold text-obsidian transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {sending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Talebi Gönder
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
